import { useEffect, useState } from 'react';
import TableHeader from '../../components/generic/TableHeader';
import { cachedGet } from '../../lib/requestDedupe';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import {
  BookmarkSimpleIcon,
  CheckCircleIcon,
  CheckIcon,
  EyeSlashIcon,
  SmileySadIcon,
  SmileyXEyesIcon,
  TimerIcon,
  WarningOctagonIcon,
  XCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import AllQuestionAnalysisChart from './charts/AllQuestionAnalysisChart';
import PainfulQuestionsChart from './charts/PainfulQuestionsChart';
import AllQuestionsTable from './tables/AllQuestionsTable';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function QuestionByQuestionAnalysis({ packId, testId, testIdTwo, isCombined = false, testsData }) {
  const [allQuestionAnalysis, setAllQuestionAnalysis] = useState(null);
  const [allQuestionsDetailedAnalysis, setAllQuestionsDetailedAnalysis] = useState(null);
  const [questionAttemptAnalysis, setQuestionAttemptAnalysis] = useState(null);
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showAttemptAnalysis, setShowAttemptAnalysis] = useState(false);
  const [selectedTestIndex, setSelectedTestIndex] = useState(0);
  const [currentTestId, setCurrentTestId] = useState(testId);
  const [tests, setTests] = useState([]);

  useEffect(() => {
    if (testsData && Array.isArray(testsData) && testsData.length > 0) {
      setTests(testsData);
      setCurrentTestId(testsData[0]?.testId);
      setSelectedTestIndex(0);
    } else {
      setCurrentTestId(testId);
    }
  }, [testsData, testId]);

  const handleTestChange = (index) => {
    setSelectedTestIndex(index);
    setCurrentTestId(tests[index]?.testId);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const res = await cachedGet(`/api/test/analysis/v2/${packId}/${currentTestId}/allQuestionAnalysis`);
        const response = res?.data?.data;
        const allQuestionsAnalysis = response?.allQuestionsAnalysis;
        const allQuestionsDetailedAnalysis = response?.allQuestionsDetailedAnalysis;
        setAllQuestionAnalysis(allQuestionsAnalysis);
        allQuestionsDetailedAnalysis?.forEach((item, index) => {
          item.questionNumber = index + 1;
        });
        setAllQuestionsDetailedAnalysis(allQuestionsDetailedAnalysis);
        // prepare subject meta
        const subjectMeta = [];
        allQuestionsAnalysis?.forEach((item) => {
          subjectMeta.push({
            color: item.color,
            gradient: item.gradient,
            gradientDark: item.gradientDark,
            icon: item.icon,
            title: item.subject,
          });
        });
        if (subjectMeta.length > 1) {
          subjectMeta.unshift({
            title: 'All Subjects',
            icon: 'https://cdn.quizrr.in/web-assets/analysis-v3/subjects/overall_2.svg',
            color: '#364FC7',
            gradient: 'linear-gradient(180deg, rgba(255, 255, 255, 0.60) 0%, rgba(255, 255, 255, 0.90) 100%), #364FC7',
            gradientDark:
              'linear-gradient(180deg, rgba(29, 40, 58, 0.60) 0%, rgba(29, 40, 58, 0.90) 100%), var(--Physics, #37B24D)',
          });
        }
        setSelectedSubject(subjectMeta[0]);
        setSubjectMeta(subjectMeta);

        // prepare question attempt analysis
        const questionAttemptAnalysisMap = {};
        allQuestionsDetailedAnalysis?.forEach((item, index) => {
          questionAttemptAnalysisMap[index + 1] = item?.attemptOverview;
        });
        setQuestionAttemptAnalysis(questionAttemptAnalysisMap);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [packId, currentTestId]);

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  const handleShowAttemptAnalysis = () => {
    setShowAttemptAnalysis(!showAttemptAnalysis);
  };

  const renderTableLegend = () => {
    return (
      <div className="flex flex-row items-center gap-4 min-w-max">
        {!showAttemptAnalysis ? (
          <>
            <div className="flex flex-row items-center gap-2">
              <CheckIcon className="text-[var(--q3-green-normal)]" size={20} weight="bold" />
              <p className="text-xs">Correct</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <XIcon className="text-[var(--q3-danger-normal)]" size={20} weight="bold" />
              <p className="text-xs">Wrong</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <EyeSlashIcon className="text-[var(--q3-neutral-light)]" size={20} weight="bold" />
              <p className="text-xs">Not Visited</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <WarningOctagonIcon className="text-[var(--q3-neutral-light)]" size={20} weight="bold" />
              <p className="text-xs">Not Answered</p>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-row items-center gap-2">
              <CheckCircleIcon className="text-[var(--q3-green-normal)]" size={20} />
              <p className="text-xs">Perfect</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <XCircleIcon className="text-[var(--q3-danger-normal)]" size={20} />
              <p className="text-xs">Wasted</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <TimerIcon className="text-[var(--q3-orange-normal)]" size={20} />
              <p className="text-xs">Overtime</p>
            </div>
            <div className="flex flex-row items-center gap-2">
              <SmileyXEyesIcon className="text-[var(--q3-neutral-light)]" size={20} />
              <p className="text-xs">Confused</p>
            </div>
          </>
        )}
        <div className="flex flex-row items-center gap-2">
          <BookmarkSimpleIcon className="text-[var(--q3-accent-normal)]" size={20} weight="fill" />
          <p className="text-xs">Marked for Review</p>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <TestAnalysisLoader />;
  }

  return (
    <div className="space-y-8">
      {isCombined && tests.length > 0 && (
        <div className="flex gap-3 mb-4 pb-0">
          {tests.map((test, index) => (
            <button
              key={index}
              onClick={() => handleTestChange(index)}
              className={`px-3 py-2 font-medium text-sm transition-colors duration-200 border border-[var(--q3-stroke-normal)] rounded-4xl cursor-pointer ${
                selectedTestIndex === index
                  ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                  : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)]'
              }`}
            >
              {test.testName}
            </button>
          ))}
        </div>
      )}

      <TableHeader
        headerTitle={'All Question Analysis'}
        headerTitleClass={'border-b-0'}
        headerSwitch={'Show Quality of Attempt'}
        headerSwitchCase={showAttemptAnalysis}
        headerSwitchHandler={handleShowAttemptAnalysis}
      >
        <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
          <TabsContainer color={selectedSubject?.color}>
            {subjectMeta?.map((tab) => (
              <Tab
                key={tab.title}
                isActive={selectedSubject?.title === tab.title}
                color={selectedSubject?.title === tab.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                tabColor={tab.color}
                bgColor="var(--q3-surface-dim)"
                onClick={() => handleSubjectChange(tab)}
              >
                {tab.icon && <img src={tab.icon} alt={tab.title} className="" />}
                <p className="mb-0">{tab.title}</p>
              </Tab>
            ))}
          </TabsContainer>
        </div>
        <div className="flex flex-row bg-[var(--q3-surface-dim)] gap-6 py-4 px-6 overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
          {renderTableLegend()}
        </div>
        <AllQuestionAnalysisChart
          allQuestionAnalysis={allQuestionAnalysis}
          selectedSubject={selectedSubject}
          showAttemptAnalysis={showAttemptAnalysis}
          questionAttemptAnalysis={questionAttemptAnalysis}
          packId={packId}
          testId={currentTestId}
        />
      </TableHeader>

      <TableHeader
        headerTitle={'Painful Questions'}
        headerTitleClass={'border-b-0'}
        headerDescription={
          'This section categorizes the question(s) you got wrong in the test as well as spent more than the alloted time, into respective chapters & topics so that you can analyze your weakness and work on it.'
        }
        headerSwitch={'Show Quality of Attempt'}
        headerSwitchHandler={handleShowAttemptAnalysis}
        headerSwitchCase={showAttemptAnalysis}
        headerIcon={
          <div className="bg-[var(--q3-danger-light)] rounded-full p-[1px]">
            <SmileySadIcon size={24} className="text-[var(--q3-danger-normal)]" />
          </div>
        }
      >
        <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
          <TabsContainer color={selectedSubject?.color}>
            {subjectMeta?.map((tab) => (
              <Tab
                key={tab.title}
                isActive={selectedSubject?.title === tab.title}
                color={selectedSubject?.title === tab.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                tabColor={tab.color}
                bgColor="var(--q3-surface-dim)"
                onClick={() => handleSubjectChange(tab)}
              >
                {tab.icon && <img src={tab.icon} alt={tab.title} className="" />}
                <p className="mb-0">{tab.title}</p>
              </Tab>
            ))}
          </TabsContainer>
        </div>
        <PainfulQuestionsChart
          allQuestionAnalysis={allQuestionAnalysis}
          selectedSubject={selectedSubject}
          showAttemptAnalysis={showAttemptAnalysis}
          questionAttemptAnalysis={questionAttemptAnalysis}
          packId={packId}
          testId={currentTestId}
        />
      </TableHeader>
      <TableHeader
        headerTitle={'Complete Breakdown'}
        headerDescription={
          'This table lists all the questions and your performance of each question. You can click on any row to view the question'
        }
      >
        <AllQuestionsTable
          allQuestionsDetailedAnalysis={allQuestionsDetailedAnalysis}
          packId={packId}
          testId={currentTestId}
        />
      </TableHeader>
    </div>
  );
}
